import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { apiService, Transaction, CreateTransactionRequest, AccountBalance } from '../services/api';
import Modal from './Modal';
import ConfirmModal from './ConfirmModal';
import EmptyState from './EmptyState';
import Loader from './Loader';
import { useNotifications } from './NotificationSystem';
import { handleApiError, trimFormData } from '../utils/errorHandler';
import './Transactions.css';

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<AccountBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [transactionType, setTransactionType] = useState<'cashin' | 'cashout' | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [parties, setParties] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; transactionId: number | null }>({
    isOpen: false,
    transactionId: null
  });
  const { showSuccess, showError } = useNotifications();
  const showErrorRef = useRef(showError);
  const [formData, setFormData] = useState<CreateTransactionRequest>({
    entryDate: new Date().toISOString().split('T')[0],
    accountId: 1,
    description: '',
    amount: 0,
    type: 2, // 1=Income, 2=Expense, 3=Transfer
    category: '',
    counterparty: '',
    narration: '',
    reference: '',
    tags: ''
  });
  
  // Refs to track the "add new" input values
  const newCategoryInputRef = useRef<HTMLInputElement>(null);
  const newPartyInputRef = useRef<HTMLInputElement>(null);
  const hasLoadedInitialDataRef = useRef(false);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadCategoriesAndParties = useCallback((transactions: Transaction[]) => {
    const uniqueCategories = Array.from(new Set(transactions
      .map(t => t.category)
      .filter((c): c is string => c !== undefined && c !== null && c.trim() !== '')
    )).sort();
    
    const uniqueParties = Array.from(new Set(transactions
      .map(t => t.counterparty)
      .filter((p): p is string => p !== undefined && p !== null && p.trim() !== '')
    )).sort();
    
    setCategories(uniqueCategories);
    setParties(uniqueParties);
  }, []);

  const fetchData = useCallback(async () => {
    // CRITICAL: Don't make API calls if not authenticated
    if (!apiService.isAuthenticated()) {
      console.warn('Transactions: Not authenticated, skipping fetchData');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [allTransactionsData, accountsData] = await Promise.all([
        apiService.getTransactions({ pageSize: 1000 }),
        apiService.getAccounts()
      ]);
      
      setTransactions(allTransactionsData.transactions);
      setAccounts(accountsData);
      
      // Set default account ID to the first available account
      if (accountsData.length > 0) {
        setFormData(prev => ({ ...prev, accountId: accountsData[0].id }));
      }
      
      // Load categories and parties from all transactions
      loadCategoriesAndParties(allTransactionsData.transactions);
    } catch (error: any) {
      // If 401, the global handler will redirect - don't show error
      if (error.status === 401 || error.errorCode === 'UNAUTHORIZED') {
        console.log('Transactions: 401 detected, redirecting to login');
        return;
      }
      const appError = handleApiError(error);
      showErrorRef.current('Loading Error', appError.message);
    } finally {
      setLoading(false);
    }
  }, [loadCategoriesAndParties]);

  const fetchTransactions = useCallback(async () => {
    try {
      const allTransactionsResponse = await apiService.getTransactions({ pageSize: 1000 });
      setTransactions(allTransactionsResponse.transactions);
      loadCategoriesAndParties(allTransactionsResponse.transactions);
    } catch (error) {
      const appError = handleApiError(error);
      showErrorRef.current('Loading Error', appError.message);
    }
  }, [loadCategoriesAndParties]);

  useEffect(() => {
    // CRITICAL: Don't make API calls if not authenticated
    if (!apiService.isAuthenticated()) {
      console.warn('Transactions: Not authenticated, skipping API calls');
      setLoading(false);
      return;
    }

    // Prevent duplicate initial load requests on rerenders.
    if (hasLoadedInitialDataRef.current) {
      return;
    }
    hasLoadedInitialDataRef.current = true;

    fetchData();
  }, [fetchData]);

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  /**
   * Ensures a category is selected. If a new category name is provided in the input,
   * it creates/selects it. Returns the category name that should be used.
   */
  const ensureCategorySelected = (): string | undefined => {
    const newCategoryInput = newCategoryInputRef.current?.value.trim() || '';
    const currentCategory = formData.category?.trim() || '';
    
    // If a category is already selected, use it
    if (currentCategory) {
      return currentCategory;
    }
    
    // If there's a new category input value
    if (newCategoryInput) {
      // Check if it already exists (case-insensitive)
      const existingCategory = categories.find(
        c => c.toLowerCase() === newCategoryInput.toLowerCase()
      );
      
      if (existingCategory) {
        // Category exists, just select it
        setFormData(prev => ({ ...prev, category: existingCategory }));
        if (newCategoryInputRef.current) {
          newCategoryInputRef.current.value = '';
        }
        return existingCategory;
      } else {
        // New category, add it to the list and select it
        const newCategory = newCategoryInput;
        setCategories(prev => [...prev, newCategory].sort());
        setFormData(prev => ({ ...prev, category: newCategory }));
        if (newCategoryInputRef.current) {
          newCategoryInputRef.current.value = '';
        }
        return newCategory;
      }
    }
    
    return currentCategory || undefined;
  };

  /**
   * Ensures a party is selected. If a new party name is provided in the input,
   * it creates/selects it. Returns the party name that should be used.
   */
  const ensurePartySelected = (): string | undefined => {
    const newPartyInput = newPartyInputRef.current?.value.trim() || '';
    const currentParty = formData.counterparty?.trim() || '';
    
    // If a party is already selected, use it
    if (currentParty) {
      return currentParty;
    }
    
    // If there's a new party input value
    if (newPartyInput) {
      // Check if it already exists (case-insensitive)
      const existingParty = parties.find(
        p => p.toLowerCase() === newPartyInput.toLowerCase()
      );
      
      if (existingParty) {
        // Party exists, just select it
        setFormData(prev => ({ ...prev, counterparty: existingParty }));
        if (newPartyInputRef.current) {
          newPartyInputRef.current.value = '';
        }
        return existingParty;
      } else {
        // New party, add it to the list and select it
        const newParty = newPartyInput;
        setParties(prev => [...prev, newParty].sort());
        setFormData(prev => ({ ...prev, counterparty: newParty }));
        if (newPartyInputRef.current) {
          newPartyInputRef.current.value = '';
        }
        return newParty;
      }
    }
    
    return currentParty || undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Ensure category and party are created/selected before submitting
      // This handles the case where user types in "add new" inputs but doesn't press Enter
      const selectedCategory = ensureCategorySelected();
      const selectedParty = ensurePartySelected();
      
      // Update formData with the ensured category and party values
      // The helpers already update formData state, but we need to use the returned values
      // to ensure we have the latest data for the API call
      const updatedFormData = {
        ...formData,
        category: selectedCategory || formData.category,
        counterparty: selectedParty || formData.counterparty,
      };
      
      // Trim form data and set transaction type based on the selected button
      const trimmedData = trimFormData(updatedFormData);
      const transactionData = {
        ...trimmedData,
        type: transactionType === 'cashin' ? 1 : 2 // 1=Income, 2=Expense
      };
      
      if (editingTransaction) {
        await apiService.updateTransaction(editingTransaction.id, transactionData);
      } else {
        await apiService.createTransaction(transactionData);
      }
      
      // Update local categories and parties lists with newly saved values
      const newCategory = transactionData.category?.trim();
      const newParty = transactionData.counterparty?.trim();
      
      if (newCategory && !categories.includes(newCategory)) {
        setCategories(prev => [...prev, newCategory].sort());
      }
      if (newParty && !parties.includes(newParty)) {
        setParties(prev => [...prev, newParty].sort());
      }
      
      setShowAddForm(false);
      setEditingTransaction(null);
      setTransactionType(null);
      setFormData({
        entryDate: new Date().toISOString().split('T')[0],
        accountId: accounts.length > 0 ? accounts[0].id : 1,
        description: '',
        amount: 0,
        type: 2, // 1=Income, 2=Expense, 3=Transfer
        category: '',
        counterparty: '',
        narration: '',
        reference: '',
        tags: ''
      });
      
      // Clear the "add new" input refs
      if (newCategoryInputRef.current) {
        newCategoryInputRef.current.value = '';
      }
      if (newPartyInputRef.current) {
        newPartyInputRef.current.value = '';
      }
      
      // Refresh transactions and categories/parties from server
      await fetchTransactions();
      showSuccess(
        'Transaction Saved',
        editingTransaction ? 'Transaction updated successfully' : 'Transaction added successfully'
      );
    } catch (error) {
      const appError = handleApiError(error);
      showError('Transaction Error', appError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setTransactionType(transaction.type === 1 ? 'cashin' : 'cashout');
    setFormData({
      entryDate: transaction.entryDate.split('T')[0],
      accountId: transaction.accountId,
      description: transaction.description,
      amount: transaction.amount,
      type: transaction.type,
      category: transaction.category || '',
      counterparty: transaction.counterparty || '',
      narration: transaction.narration || '',
      reference: transaction.reference || '',
      tags: transaction.tags || ''
    });
    setShowAddForm(true);
    // Clear the "add new" input refs when editing
    if (newCategoryInputRef.current) {
      newCategoryInputRef.current.value = '';
    }
    if (newPartyInputRef.current) {
      newPartyInputRef.current.value = '';
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeleteConfirm({ isOpen: true, transactionId: id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.transactionId) return;
    
    try {
      await apiService.deleteTransaction(deleteConfirm.transactionId);
      await fetchTransactions();
      showSuccess('Transaction Deleted', 'Transaction deleted successfully');
      setDeleteConfirm({ isOpen: false, transactionId: null });
    } catch (error) {
      const appError = handleApiError(error);
      showError('Delete Error', appError.message);
      setDeleteConfirm({ isOpen: false, transactionId: null });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter transactions based on date range and search
  const filteredTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) return [];

    let list = [...transactions];

    // Date filtering
    if (fromDate) {
      const d = new Date(fromDate);
      d.setHours(0, 0, 0, 0);
      list = list.filter(t => {
        const tDate = new Date(t.entryDate);
        tDate.setHours(0, 0, 0, 0);
        return tDate >= d;
      });
    }
    if (toDate) {
      const d = new Date(toDate);
      d.setHours(23, 59, 59, 999);
      list = list.filter(t => {
        const tDate = new Date(t.entryDate);
        return tDate <= d;
      });
    }

    // Search filtering
    if (debouncedSearch.trim() !== '') {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(t =>
        String(t.description || '').toLowerCase().includes(q) ||
        String(t.reference || '').toLowerCase().includes(q) ||
        String(t.accountName || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [transactions, fromDate, toDate, debouncedSearch]);

  if (loading) {
    return (
      <div className="transactions">
        <Loader message="Loading transactions..." size="large" />
      </div>
    );
  }

  return (
    <div className="transactions">
      <div className="transactions-header">
        <h1>Transactions</h1>
        <div className="transaction-buttons">
          <button 
            className="cash-in-btn"
            onClick={() => {
              setTransactionType('cashin');
              setShowAddForm(true);
              setEditingTransaction(null);
              setFormData({
                entryDate: new Date().toISOString().split('T')[0],
                accountId: accounts.length > 0 ? accounts[0].id : 1,
                description: '',
                amount: 0,
                type: 1, // 1=Income
                category: '',
                counterparty: '',
                narration: '',
                reference: '',
                tags: ''
              });
              // Clear the "add new" input refs
              if (newCategoryInputRef.current) {
                newCategoryInputRef.current.value = '';
              }
              if (newPartyInputRef.current) {
                newPartyInputRef.current.value = '';
              }
            }}
          >
            <span>💰</span>
            Cash In
          </button>
          <button 
            className="cash-out-btn"
            onClick={() => {
              setTransactionType('cashout');
              setShowAddForm(true);
              setEditingTransaction(null);
              setFormData({
                entryDate: new Date().toISOString().split('T')[0],
                accountId: accounts.length > 0 ? accounts[0].id : 1,
                description: '',
                amount: 0,
                type: 2, // 2=Expense
                category: '',
                counterparty: '',
                narration: '',
                reference: '',
                tags: ''
              });
              // Clear the "add new" input refs
              if (newCategoryInputRef.current) {
                newCategoryInputRef.current.value = '';
              }
              if (newPartyInputRef.current) {
                newPartyInputRef.current.value = '';
              }
            }}
          >
            <span>💸</span>
            Cash Out
          </button>
        </div>
      </div>

      <Modal
        isOpen={showAddForm}
        onClose={() => {
          setShowAddForm(false);
          setEditingTransaction(null);
          setTransactionType(null);
          // Clear the "add new" input refs when closing
          if (newCategoryInputRef.current) {
            newCategoryInputRef.current.value = '';
          }
          if (newPartyInputRef.current) {
            newPartyInputRef.current.value = '';
          }
        }}
        ariaLabel={editingTransaction ? 'Edit Transaction' : 
                   transactionType === 'cashin' ? 'Cash In Transaction' : 
                   transactionType === 'cashout' ? 'Cash Out Transaction' : 'Add New Transaction'}
        className="transaction-form"
      >
        <div className="form-header">
          <h2>
            {editingTransaction ? 'Edit Transaction' : 
             transactionType === 'cashin' ? 'Cash In Transaction' : 
             transactionType === 'cashout' ? 'Cash Out Transaction' : 'Add New Transaction'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={formData.entryDate}
                    onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Account</label>
                  <select
                    value={formData.accountId}
                    onChange={(e) => setFormData({ ...formData, accountId: parseInt(e.target.value) })}
                    required
                  >
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({account.accountType}) - {formatCurrency(account.balance)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Transaction Type</label>
                  <div className="transaction-type-display">
                    <span className={`type-badge ${transactionType === 'cashin' ? 'cashin' : 'cashout'}`}>
                      {transactionType === 'cashin' ? '💰 Cash In' : '💸 Cash Out'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter transaction description"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <div className="dropdown-with-add">
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    <input
                      ref={newCategoryInputRef}
                      type="text"
                      placeholder="Or add new category"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // Use the same helper function to ensure consistency
                          ensureCategorySelected();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Party</label>
                  <div className="dropdown-with-add">
                    <select
                      value={formData.counterparty}
                      onChange={(e) => setFormData({ ...formData, counterparty: e.target.value })}
                    >
                      <option value="">Select Party</option>
                      {parties.map((party) => (
                        <option key={party} value={party}>
                          {party}
                        </option>
                      ))}
                    </select>
                    <input
                      ref={newPartyInputRef}
                      type="text"
                      placeholder="Or add new party"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // Use the same helper function to ensure consistency
                          ensurePartySelected();
                        }
                      }}
                    />
                  </div>
                </div>
              </div>


              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.narration}
                  onChange={(e) => setFormData({ ...formData, narration: e.target.value })}
                  placeholder="Additional notes about this transaction"
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowAddForm(false)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  {isSubmitting 
                    ? (editingTransaction ? 'Updating...' : 'Adding...') 
                    : (editingTransaction ? 'Update Transaction' : 'Add Transaction')}
                </button>
              </div>
            </form>
      </Modal>
      
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, transactionId: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Transaction"
        message="Are you sure you want to delete this transaction? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
      />

      <div className="transactions-content">
        {!Array.isArray(transactions) || transactions.length === 0 ? (
          <EmptyState
            icon="💰"
            title="No transactions yet"
            message="Start by adding your first transaction to track your income and expenses."
            actionLabel="Add Transaction"
            onAction={() => {
              setTransactionType('cashin');
              setShowAddForm(true);
            }}
          />
        ) : filteredTransactions.length === 0 ? (
          <EmptyState
            icon="🔍"
            title="No transactions found"
            message="Try adjusting your filters or search terms to find transactions."
          />
        ) : (
          <>
            <div className="transactions-filters">
              <div className="filter-left">
                <label>
                  From:
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    aria-label="Filter transactions from date"
                  />
                </label>
                <label>
                  To:
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    aria-label="Filter transactions to date"
                  />
                </label>
              </div>
              <div className="filter-right">
                <input
                  type="text"
                  placeholder="Search description, reference, account..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search transactions"
                />
              </div>
            </div>
            <div className="transactions-table-wrapper">
              <table className="rokada-transactions-table" role="table">
                <thead>
                  <tr>
                    <th scope="col" className="col-date">Date</th>
                    <th scope="col" className="col-description">Description</th>
                    <th scope="col" className="col-debit">Sent</th>
                    <th scope="col" className="col-credit">Received</th>
                    <th scope="col" className="col-balance">Balance</th>
                    <th scope="col" className="col-actions">Actions</th>
                  </tr>
                </thead>
              <tbody>
                {(() => {
                  let runningBalance = 0;
                  return filteredTransactions.map((transaction) => {
                    const debit = transaction.type === 2 ? transaction.amount : 0;
                    const credit = transaction.type === 1 ? transaction.amount : 0;
                    runningBalance += credit - debit;
                    
                    return (
                      <tr key={transaction.id} className="transaction-row">
                        <td className="col-date">{formatDate(transaction.entryDate)}</td>
                        <td className="col-description">
                          <div className="description-primary">{transaction.description}</div>
                          {(transaction.category || transaction.counterparty) && (
                            <div className="description-secondary">
                              {transaction.category && <span>{transaction.category}</span>}
                              {transaction.category && transaction.counterparty && <span> • </span>}
                              {transaction.counterparty && <span>{transaction.counterparty}</span>}
                            </div>
                          )}
                        </td>
                        <td className="col-debit">{debit > 0 ? formatCurrency(debit) : '-'}</td>
                        <td className="col-credit">{credit > 0 ? formatCurrency(credit) : '-'}</td>
                        <td className="col-balance">{formatCurrency(runningBalance)}</td>
                        <td className="col-actions">
                          <button 
                            className="action-btn edit-btn" 
                            onClick={() => handleEdit(transaction)}
                            aria-label="Edit transaction"
                            title="Edit transaction"
                          >
                            ✏️
                          </button>
                          <button 
                            className="action-btn delete-btn" 
                            onClick={() => handleDeleteClick(transaction.id)}
                            aria-label="Delete transaction"
                            title="Delete transaction"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
              <tfoot>
                <tr className="summary-row">
                  <td colSpan={2} className="summary-label">Total</td>
                  <td className="col-debit summary-value">
                    {formatCurrency(filteredTransactions.filter(t => t.type === 2).reduce((sum, t) => sum + t.amount, 0))}
                  </td>
                  <td className="col-credit summary-value">
                    {formatCurrency(filteredTransactions.filter(t => t.type === 1).reduce((sum, t) => sum + t.amount, 0))}
                  </td>
                  <td className="col-balance summary-value">
                    {formatCurrency(filteredTransactions.reduce((sum, t) => sum + (t.type === 1 ? t.amount : -t.amount), 0))}
                  </td>
                  <td className="col-actions"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Transactions;